import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { createSocketClient, disconnectSocket } from "utils/socket/socketClient";

// Components & Icons
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Redux & Utilities
import exportImportSampleFileThunk from "redux/Thunks/Other";
import { paramCreater } from "utils/methods/methods";
import Constants, { Common, Icons } from "utils/Constants";
import { openSnackbar } from "redux/Slice/Notification";
import Sessions from "utils/Sessions";
import ApiService from "redux/ApiService/ApiService";
import {
  buildDownloadProxyUrl,
  fetchPdfBytesFromUrl,
  getPdfBytes,
  PDF_STATUS_POLL_INTERVAL_MS,
  PDF_STATUS_POLL_TIMEOUT_MS,
  PDF_SOCKET_TIMEOUT_MS,
  pollPdfJobUntilReady,
  triggerPdfDownload,
} from "utils/pdfExport";
import { downloadBackgroundExcelJob } from "utils/excelExport";

const ZIP_SOCKET_TIMEOUT_MS = 1500000;
const ZIP_DEDUPE_LOCAL_URL_MS = 15000;
const ZIP_DEDUPE_GLOBAL_URL_MS = 20000;
const ZIP_DEDUPE_GLOBAL_ANY_MS = 1500;
const DOWNLOAD_CLEANUP_DELAY_MS = 1000;
const IFRAME_CLEANUP_DELAY_MS = 10000;
const DEFAULT_ZIP_FILE_NAME = "Reports.zip";
const DEFAULT_REPORT_IMAGES_ZIP_FILE_NAME = "Report_Images.zip";

const triggerBlobDownload = (blobData, fileName) => {
  const url = window.URL.createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(url);
  }, DOWNLOAD_CLEANUP_DELAY_MS);
};

const pruneRecentUrls = (recentUrls, ttlMs, now) => {
  recentUrls.forEach((timestamp, url) => {
    if (now - timestamp >= ttlMs) {
      recentUrls.delete(url);
    }
  });
};

const parseContentDispositionFileName = (headerValue, fallback = DEFAULT_ZIP_FILE_NAME) => {
  if (!headerValue || typeof headerValue !== "string") return fallback;
  const match = headerValue.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
  if (!match || !match[1]) return fallback;
  try {
    return decodeURIComponent(match[1].replace(/"/g, ""));
  } catch (error) {
    return match[1].replace(/"/g, "");
  }
};

const pluralizeImages = (count) => `${count} image${Number(count) === 1 ? "" : "s"}`;

const buildImageLimitErrorMessage = (payload) => {
  const details = payload?.data || payload || {};
  const imageCount = details?.imageCount;
  const imageLimit = details?.imageLimit;
  const fallbackMessage = payload?.message || payload?.msg;
  const hasImageLimitDetails = imageCount !== undefined && imageLimit !== undefined;

  if (
    hasImageLimitDetails &&
    Number.isFinite(Number(imageCount)) &&
    Number.isFinite(Number(imageLimit))
  ) {
    return (
      fallbackMessage ||
      `This export contains ${pluralizeImages(
        Number(imageCount)
      )}, but the ZIP export limit is ${pluralizeImages(
        Number(imageLimit)
      )}. Please narrow your filters and try again.`
    );
  }

  return null;
};

const resolveErrorMessage = async (responseData) => {
  if (!responseData) return Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE;
  if (typeof responseData === "string") return responseData;
  const structuredMessage = buildImageLimitErrorMessage(responseData);
  if (structuredMessage) return structuredMessage;
  if (responseData?.message) return responseData.message;
  if (responseData?.msg) return responseData.msg;
  if (responseData instanceof ArrayBuffer) {
    try {
      const decodedText = new TextDecoder().decode(responseData);
      const parsedData = JSON.parse(decodedText);
      return (
        buildImageLimitErrorMessage(parsedData) ||
        parsedData?.message ||
        parsedData?.msg ||
        decodedText ||
        Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE
      );
    } catch (error) {
      return Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE;
    }
  }
  if (typeof responseData?.text === "function") {
    try {
      const text = await responseData.text();
      if (!text) return Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE;
      try {
        const parsed = JSON.parse(text);
        return (
          buildImageLimitErrorMessage(parsed) ||
          parsed?.message ||
          parsed?.msg ||
          text ||
          Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE
        );
      } catch (error) {
        return text || Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE;
      }
    } catch (error) {
      return Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE;
    }
  }
  return Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE;
};

const getJsonPayload = async (responseData, responseStatus) => {
  if (!responseData) return null;
  if (typeof responseData === "object" && !(responseData instanceof Blob)) {
    return responseData;
  }
  if (typeof responseData?.text === "function") {
    const contentType = responseData?.type || "";
    const looksLikeJson =
      contentType.includes("application/json") ||
      contentType.includes("text/json") ||
      responseStatus === Common.API_STATUS_202;
    if (!looksLikeJson) return null;
    try {
      const text = await responseData.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }
  return null;
};

const resolveApiUrlWithBase = (baseUrl, url) => {
  if (!url || typeof url !== "string") return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (!baseUrl) return url;

  try {
    const parsedBase = new URL(baseUrl);
    const normalizedPath = url.startsWith("/") ? url : `/${url}`;
    const basePath = (parsedBase.pathname || "").replace(/\/$/, "");
    let finalPath = normalizedPath;

    if (basePath === "/api" && normalizedPath.startsWith("/api/")) {
      finalPath = normalizedPath.replace(/^\/api\//, "/");
    }

    return `${parsedBase.origin}${basePath}${finalPath}`;
  } catch (error) {
    const normalizedBase = baseUrl.replace(/\/$/, "");
    const normalizedPath = url.startsWith("/") ? url : `/${url}`;
    if (normalizedBase.endsWith("/api") && normalizedPath.startsWith("/api/")) {
      return `${normalizedBase}${normalizedPath.replace(/^\/api/, "")}`;
    }
    return `${normalizedBase}${normalizedPath}`;
  }
};

const resolvePdfExportErrorMessage = (result) =>
  result?.payload?.message || result?.error?.message || Constants.PDF_GENERATION_FAILED;

function ExportHOC(WrappedComponent) {
  return function ExportFile(props) {
    const dispatch = useDispatch();
    const [exportLoading, setExportLoading] = useState(false);
    const zipDownloadGuardRef = useRef({
      activeJobs: new Set(),
      completedJobs: new Set(),
      recentUrls: new Map(),
    });
    const getGlobalZipGuard = () => {
      const root = typeof window !== "undefined" ? window : globalThis;
      if (!root.zipExportDownloadGuardState) {
        root.zipExportDownloadGuardState = {
          activeJobs: new Set(),
          completedJobs: new Set(),
          recentUrls: new Map(),
          lastAnyDownloadAt: 0,
        };
      }
      return root.zipExportDownloadGuardState;
    };

    const handleEquipmentSampleFileExport = async (exportFileName, root) => {
      setExportLoading(true);
      const currentDate = new Date();
      const fileName = `Reynard_${root}_${currentDate.getFullYear()}-${
        currentDate.getMonth() + 1
      }-${currentDate.getDate()}_${currentDate.getHours()}-${currentDate.getMinutes()}-${currentDate.getSeconds()}.${
        exportFileName.split(".")[1]
      }`;

      const res = await dispatch(exportImportSampleFileThunk(exportFileName));
      const url = window.URL.createObjectURL(res.payload);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setExportLoading(false);
    };

    const handleBackgroundPdfDownload = async (jobId, socketUrl, filename, jobMeta = {}) => {
      const socket = createSocketClient(socketUrl);
      let settled = false;
      let timeoutId = null;
      let fallbackInProgress = false;
      let completionReceived = false;
      const downloadPdfBytes = getPdfBytes(jobId, socketUrl, Constants.PDF_GENERATION_FAILED);
      const withTimestamp = (url) => `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
      const getPreferredStatusUrl = () =>
        jobMeta?.statusUrl || `${socketUrl.replace(/\/$/, "")}/api/pdf/pdf-status/${jobId}`;
      const getPreferredDownloadUrl = (data = null) =>
        jobMeta?.downloadUrl || data?.downloadUrl || buildDownloadProxyUrl(socketUrl, jobId);
      const getPreferredFileName = (data = null) => data?.fileName || jobMeta?.fileName || filename;
      const sleep = (ms) =>
        new Promise((resolve) => {
          setTimeout(resolve, ms);
        });

      const settle = (options = {}) => {
        if (settled) return;
        settled = true;
        if (timeoutId) clearTimeout(timeoutId);
        disconnectSocket(socket);
        if (options.message) {
          dispatch(
            openSnackbar({ message: options.message, notificationType: options.type || "error" })
          );
        }
        setExportLoading(false);
      };

      const fetchStatusPayload = async () => {
        const statusUrl = withTimestamp(getPreferredStatusUrl());
        const resp = await fetch(statusUrl, {
          headers: { Authorization: `Bearer ${Sessions.userToken}` },
        });
        if (!resp.ok) {
          // eslint-disable-next-line no-console
          console.warn("[PDF Export] Status request failed", { jobId, status: resp.status });
          return null;
        }
        return resp.json().catch(() => null);
      };

      const fetchPreferredPdfBytes = async (data = null) => {
        const preferredDownloadUrl = getPreferredDownloadUrl(data);
        if (preferredDownloadUrl && (jobMeta?.downloadUrl || data?.downloadUrl || data?.pdfUrl)) {
          return fetchPdfBytesFromUrl(preferredDownloadUrl);
        }
        return downloadPdfBytes(data || {});
      };

      const pollPreferredPdfUntilReady = async () => {
        if (!jobMeta?.statusUrl && !jobMeta?.downloadUrl) {
          return pollPdfJobUntilReady(jobId, socketUrl, Constants.PDF_GENERATION_FAILED);
        }

        const deadline = Date.now() + PDF_STATUS_POLL_TIMEOUT_MS;
        const pollOnce = async () => {
          if (Date.now() >= deadline) {
            throw new Error(Constants.PDF_EXPORT_TIMED_OUT);
          }

          const payload = await fetchStatusPayload();

          if (payload?.status === "failed") {
            throw new Error(payload?.error || Constants.PDF_GENERATION_FAILED);
          }

          if (
            payload &&
            (payload.status === "completed" || payload.status === "emailed") &&
            payload.resultUrl
          ) {
            return payload;
          }

          await sleep(PDF_STATUS_POLL_INTERVAL_MS);
          return pollOnce();
        };

        return pollOnce();
      };

      const completeDownload = async (data = null) => {
        if (data instanceof ArrayBuffer) {
          if (!data.byteLength) {
            throw new Error(Constants.PDF_EMPTY_PAYLOAD);
          }

          completionReceived = true;
          if (timeoutId) clearTimeout(timeoutId);
          disconnectSocket(socket);
          triggerPdfDownload(data, getPreferredFileName());
          dispatch(
            openSnackbar({
              message: Constants.PDF_DOWNLOAD_COMPLETE,
              notificationType: "success",
            })
          );
          settle({ type: "success" });
          return;
        }

        completionReceived = true;
        if (timeoutId) clearTimeout(timeoutId);
        disconnectSocket(socket);

        const pdfBytes = await fetchPreferredPdfBytes(data);
        if (!pdfBytes || pdfBytes.byteLength === 0) {
          throw new Error(Constants.PDF_EMPTY_PAYLOAD);
        }

        triggerPdfDownload(pdfBytes, getPreferredFileName(data));
        dispatch(
          openSnackbar({
            message: Constants.PDF_DOWNLOAD_COMPLETE,
            notificationType: "success",
          })
        );
        settle({ type: "success" });
      };

      const startPollingWatcher = async () => {
        try {
          const readyData = await pollPreferredPdfUntilReady();
          if (settled || completionReceived) return;
          await completeDownload(readyData);
        } catch (_error) {
          // eslint-disable-next-line no-console
          console.warn("[PDF Export] Polling watcher stopped without completion", {
            jobId,
            message: _error?.message,
          });
          // Keep the socket/timeout path alive; this watcher is a parallel
          // backup so we do not regress on transient local networking issues.
        }
      };

      const settleWithFallback = async (errorMessage = Constants.PDF_GENERATION_FAILED) => {
        if (settled || fallbackInProgress) return;
        fallbackInProgress = true;

        try {
          const readyData = await pollPreferredPdfUntilReady();
          if (settled) return;
          await completeDownload(readyData);
        } catch (_error) {
          settle({ message: errorMessage, type: "error" });
        } finally {
          fallbackInProgress = false;
        }
      };

      timeoutId = setTimeout(() => {
        settleWithFallback(Constants.PDF_EXPORT_TIMED_OUT);
      }, PDF_SOCKET_TIMEOUT_MS);

      // 1. Show Initial Progress
      dispatch(
        openSnackbar({
          message: (
            <MDBox display="flex" alignItems="center">
              {Icons.LOADING}
              <MDTypography
                variant="button"
                color="light"
                ml={2}
                fontWeight="regular"
                sx={{ fontSize: "14px" }}
              >
                {Constants.PDF_PREPARING}
              </MDTypography>
            </MDBox>
          ),
          notificationType: "info",
        })
      );

      startPollingWatcher();

      // Listen for progress
      socket.on(`job_progress_${jobId}`, (progress) => {
        dispatch(
          openSnackbar({
            message: (
              <MDBox display="flex" alignItems="center">
                {Icons.LOADING}
                <MDTypography
                  variant="button"
                  color="light"
                  ml={2}
                  fontWeight="regular"
                  sx={{ fontSize: "14px" }}
                >
                  {progress.message}...
                </MDTypography>
              </MDBox>
            ),
            notificationType: "info",
          })
        );
      });

      // When the socket connects, poll the job status once immediately.
      // This catches cache-hit jobs where the server emits job_completed
      // BEFORE this socket is connected (the event is broadcast-only,
      // so it's lost if no one is listening yet).
      socket.on("connect", async () => {
        if (settled || completionReceived) return;
        try {
          const payload = await fetchStatusPayload();
          if (!payload || settled || completionReceived) return;
          if (
            payload &&
            (payload.status === "completed" || payload.status === "emailed") &&
            payload.resultUrl &&
            !settled &&
            !completionReceived
          ) {
            // Job already completed before we connected — download now.
            await completeDownload(payload);
          }
        } catch (_err) {
          // If we already set completionReceived, we own the download
          // attempt — settle with error to stop the loading spinner.
          // Otherwise, the socket listener or timeout fallback will handle it.
          if (!settled) {
            settle({ message: Constants.PDF_FAILED_DOWNLOAD, type: "error" });
          }
        }
      });

      socket.on("connect_error", () => {
        // eslint-disable-next-line no-console
        console.warn("[PDF Export] Socket connect error", { jobId });
        settleWithFallback(Constants.PDF_CONNECTION_FAILED);
      });

      socket.on("connect_timeout", () => {
        settleWithFallback(Constants.PDF_CONNECTION_TIMEOUT);
      });

      socket.on("error", () => {
        settleWithFallback(Constants.PDF_SOCKET_ERROR);
      });

      socket.on("disconnect", () => {
        // Only fall back if we didn't already receive the completion event.
        // When job_completed fires, we disconnect the socket intentionally
        // before starting the async download — that disconnect is expected.
        if (!settled && !completionReceived) {
          settleWithFallback(Constants.PDF_DISCONNECTED);
        }
      });

      // Listen for completion
      socket.on(`job_completed_${jobId}`, async (data) => {
        if (settled) return;

        // Mark completion received BEFORE any async work.
        // This prevents the disconnect handler from triggering fallback
        // when we intentionally close the socket below.
        completionReceived = true;

        // Disconnect socket immediately — we have the data we need.
        // Keeping it open during the async download wastes a browser
        // connection slot (browsers limit to 6 per origin on HTTP/1.1).
        if (timeoutId) clearTimeout(timeoutId);
        disconnectSocket(socket);

        if (!data.success) {
          settle({ message: Constants.PDF_GENERATION_FAILED, type: "error" });
          return;
        }

        if (data.emailed || data.emailAttempted) {
          dispatch(
            openSnackbar({
              message: Constants.PDF_EMAIL_SENT_HEAVY_LOAD,
              notificationType: "success",
            })
          );
          settle({ type: "success" });
          return;
        }

        try {
          await completeDownload(data);
        } catch (err) {
          dispatch(
            openSnackbar({ message: Constants.PDF_FAILED_DOWNLOAD, notificationType: "error" })
          );
          settle({ message: Constants.PDF_FAILED_DOWNLOAD, type: "error" });
        }
      });
    };

    const handlePdfExport = async (fileName, exportThunk, dateFormat = true) => {
      setExportLoading(true);
      let name = fileName;
      if (dateFormat) {
        const currentDate = new Date();
        name = `Reynard_${fileName}_${currentDate.getFullYear()}-${
          currentDate.getMonth() + 1
        }-${currentDate.getDate()}_${currentDate.getHours()}-${currentDate.getMinutes()}-${currentDate.getSeconds()}.pdf`;
      }

      const res = await dispatch(exportThunk);

      if (res?.payload?.status === false) {
        setExportLoading(false);
        dispatch(
          openSnackbar({
            message: resolvePdfExportErrorMessage(res),
            notificationType: "error",
          })
        );
        return res;
      }

      if (res.error !== undefined) {
        setExportLoading(false);
        dispatch(
          openSnackbar({
            message: resolvePdfExportErrorMessage(res),
            notificationType: "error",
          })
        );
        return res;
      }

      if (!res.payload) {
        setExportLoading(false);
        dispatch(
          openSnackbar({
            message: Constants.PDF_GENERATION_FAILED,
            notificationType: "error",
          })
        );
        return res;
      }

      dispatch(
        openSnackbar({
          message: (
            <MDBox display="flex" alignItems="center">
              {Icons.LOADING}
              <MDTypography
                variant="button"
                color="light"
                ml={2}
                fontWeight="regular"
                sx={{ fontSize: "14px" }}
              >
                {Constants.PDF_PREPARING}
              </MDTypography>
            </MDBox>
          ),
          notificationType: "info",
        })
      );

      if (
        res.payload?.downloadUrl &&
        (res.payload.status === "completed" || res.payload.cacheHit)
      ) {
        const pdfBytes = await fetchPdfBytesFromUrl(res.payload.downloadUrl);
        await triggerPdfDownload(pdfBytes, res.payload.fileName || name);
        dispatch(
          openSnackbar({ message: Constants.PDF_DOWNLOAD_COMPLETE, notificationType: "success" })
        );
        setExportLoading(false);
        return res;
      }

      // Check if we got a Background Job ID instead of a Blob
      if (res.payload && res.payload.jobId && res.payload.socketUrl) {
        handleBackgroundPdfDownload(res.payload.jobId, res.payload.socketUrl, name, res.payload);
        return res;
      }

      const url = window.URL.createObjectURL(res.payload);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setExportLoading(false);
      return res;
    };

    const showExportLoadingSnackbar = (message) =>
      dispatch(
        openSnackbar({
          message: (
            <MDBox display="flex" alignItems="center">
              {Icons.LOADING}
              <MDTypography
                variant="button"
                color="light"
                ml={2}
                fontWeight="regular"
                sx={{ fontSize: "14px" }}
              >
                {message}
              </MDTypography>
            </MDBox>
          ),
          notificationType: "info",
        })
      );

    /**
     * Starts a background Excel export job and downloads the file once it is ready.
     * Resolves true when the file was downloaded (or emailed), false when the export failed.
     */
    const handleExcelExport = async (fileName, exportThunk) => {
      const res = await dispatch(exportThunk);
      const job = res?.payload?.data;

      if (res?.error || res?.payload?.status === false || !job?.jobId || !job?.socketUrl) {
        dispatch(
          openSnackbar({
            message: res?.payload?.message || Constants.EXCEL_GENERATION_FAILED,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
        return false;
      }

      showExportLoadingSnackbar(Constants.EXCEL_PREPARING);

      try {
        const { emailed } = await downloadBackgroundExcelJob({
          job,
          fallbackFileName: fileName,
          onProgress: (message) => showExportLoadingSnackbar(`${message}...`),
        });
        dispatch(
          openSnackbar({
            message: emailed
              ? Constants.EXCEL_EMAIL_SENT_HEAVY_LOAD
              : Constants.EXCEL_DOWNLOAD_COMPLETE,
            notificationType: Constants.NOTIFICATION_SUCCESS,
          })
        );
        return true;
      } catch (error) {
        dispatch(
          openSnackbar({
            message: error?.message || Constants.EXCEL_GENERATION_FAILED,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
        return false;
      }
    };

    const handleExportZipFile = async (filters, exportThunk) => {
      setExportLoading(true);
      const paramData = Object.fromEntries(
        Object.entries(filters || {}).filter(([, value]) => value !== "")
      );
      const param = paramCreater({ ...paramData, export: true });
      const apiBaseUrl = ApiService?.defaults?.baseURL || process.env.REACT_APP_BASE_URL || "";
      const resolveApiUrl = (url) => resolveApiUrlWithBase(apiBaseUrl, url);

      const downloadZipByUrl = async (downloadUrl, fileNameFallback = DEFAULT_ZIP_FILE_NAME) => {
        const resolvedDownloadUrl = resolveApiUrl(downloadUrl);
        const globalGuard = getGlobalZipGuard();
        const now = Date.now();
        pruneRecentUrls(zipDownloadGuardRef.current.recentUrls, ZIP_DEDUPE_LOCAL_URL_MS, now);
        pruneRecentUrls(globalGuard.recentUrls, ZIP_DEDUPE_GLOBAL_URL_MS, now);
        const lastTriggeredAt = zipDownloadGuardRef.current.recentUrls.get(resolvedDownloadUrl);
        const globalLastTriggeredAt = globalGuard.recentUrls.get(resolvedDownloadUrl);
        if (lastTriggeredAt && now - lastTriggeredAt < ZIP_DEDUPE_LOCAL_URL_MS) {
          return;
        }
        if (globalLastTriggeredAt && now - globalLastTriggeredAt < ZIP_DEDUPE_GLOBAL_URL_MS) {
          return;
        }
        if (
          globalGuard.lastAnyDownloadAt &&
          now - globalGuard.lastAnyDownloadAt < ZIP_DEDUPE_GLOBAL_ANY_MS
        ) {
          return;
        }
        zipDownloadGuardRef.current.recentUrls.set(resolvedDownloadUrl, now);
        globalGuard.recentUrls.set(resolvedDownloadUrl, now);
        globalGuard.lastAnyDownloadAt = now;
        if (/^https?:\/\//i.test(resolvedDownloadUrl)) {
          // Cross-origin CDN downloads can fail with fetch/CORS.
          // Trigger download once via hidden iframe to avoid duplicate downloads.
          const frame = document.createElement("iframe");
          frame.style.display = "none";
          frame.src = `${resolvedDownloadUrl}${
            resolvedDownloadUrl.includes("?") ? "&" : "?"
          }t=${Date.now()}`;
          document.body.appendChild(frame);
          setTimeout(() => frame.remove(), IFRAME_CLEANUP_DELAY_MS);
          return;
        }

        const response = await fetch(`${resolvedDownloadUrl}?t=${Date.now()}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${Sessions.userToken}` },
        });
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          let errorMessage = Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE;
          try {
            const parsed = JSON.parse(text);
            errorMessage = parsed?.message || errorMessage;
          } catch (_jsonParseError) {
            errorMessage = text || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const contentDisposition = response.headers.get("content-disposition");
        const downloadedFileName = parseContentDispositionFileName(
          contentDisposition,
          fileNameFallback
        );
        const blob = await response.blob();
        triggerBlobDownload(blob, downloadedFileName);
      };

      const handleBackgroundZipDownload = async (jobData) => {
        const jobId = jobData?.jobId;
        const globalGuard = getGlobalZipGuard();
        if (jobId && zipDownloadGuardRef.current.completedJobs.has(jobId)) {
          return;
        }
        if (jobId && globalGuard.completedJobs.has(jobId)) {
          return;
        }
        if (jobId && zipDownloadGuardRef.current.activeJobs.has(jobId)) {
          return;
        }
        if (jobId && globalGuard.activeJobs.has(jobId)) {
          return;
        }
        if (jobId) {
          zipDownloadGuardRef.current.activeJobs.add(jobId);
          globalGuard.activeJobs.add(jobId);
        }
        dispatch(
          openSnackbar({
            message: Constants.ZIP_PREPARING,
            notificationType: "info",
          })
        );
        try {
          const socketUrl = resolveApiUrl(jobData?.socketUrl);
          const trySocketFlow = Boolean(socketUrl && jobData?.jobId);

          if (!trySocketFlow) {
            throw new Error(Constants.ZIP_EXPORT_FAILED);
          }

          await new Promise((resolve, reject) => {
            const socket = createSocketClient(socketUrl);
            let settled = false;
            const timeoutId = setTimeout(() => {
              if (settled) return;
              settled = true;
              disconnectSocket(socket);
              reject(new Error(Constants.ZIP_EXPORT_TIMED_OUT));
            }, ZIP_SOCKET_TIMEOUT_MS);

            const settle = (fn) => {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              disconnectSocket(socket);
              fn();
            };

            socket.on("connect_error", () => {
              settle(() => reject(new Error(Constants.PDF_CONNECTION_FAILED)));
            });
            socket.on("connect_timeout", () => {
              settle(() => reject(new Error(Constants.PDF_CONNECTION_TIMEOUT)));
            });
            socket.on("connect", async () => {
              if (settled) return;
              try {
                const statusUrl = `${socketUrl.replace(/\/$/, "")}/api/pdf/export-job-status/${
                  jobData.jobId
                }`;
                const resp = await fetch(`${statusUrl}?t=${Date.now()}`, {
                  headers: { Authorization: `Bearer ${Sessions.userToken}` },
                });
                if (!resp.ok || settled) return;
                const payload = await resp.json().catch(() => null);
                if (!payload || settled) return;
                if (payload.status === "completed" && payload.resultUrl) {
                  settle(async () => {
                    try {
                      const ackUrl = `${socketUrl.replace(
                        /\/$/,
                        ""
                      )}/api/pdf/export-report-images-zip-ack/${jobData.jobId}`;
                      await fetch(ackUrl, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${Sessions.userToken}` },
                      }).catch(() => null);
                      await downloadZipByUrl(
                        payload.resultUrl,
                        payload.fileName || DEFAULT_REPORT_IMAGES_ZIP_FILE_NAME
                      );
                      resolve();
                    } catch (error) {
                      reject(error);
                    }
                  });
                }
              } catch (_err) {
                // Status check failed — let the socket event or timeout handle completion
              }
            });
            socket.on(`job_progress_${jobData.jobId}`, () => {});
            socket.on(`job_completed_${jobData.jobId}`, async (data) => {
              settle(async () => {
                try {
                  if (!data?.success) {
                    reject(new Error(data?.error || Constants.ZIP_EXPORT_FAILED));
                    return;
                  }
                  const socketDownloadUrl = data?.zipUrl;
                  if (!socketDownloadUrl) {
                    reject(new Error(Constants.ZIP_EXPORT_FAILED));
                    return;
                  }
                  const ackUrl = `${socketUrl.replace(
                    /\/$/,
                    ""
                  )}/api/pdf/export-report-images-zip-ack/${jobData.jobId}`;
                  await fetch(ackUrl, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${Sessions.userToken}` },
                  }).catch(() => null);
                  await downloadZipByUrl(
                    socketDownloadUrl,
                    data?.fileName || DEFAULT_REPORT_IMAGES_ZIP_FILE_NAME
                  );
                  resolve();
                } catch (error) {
                  reject(error);
                }
              });
            });
          });
          if (jobId) {
            zipDownloadGuardRef.current.completedJobs.add(jobId);
            globalGuard.completedJobs.add(jobId);
          }
          dispatch(
            openSnackbar({
              message: Constants.ZIP_DOWNLOAD_COMPLETE,
              notificationType: Constants.NOTIFICATION_SUCCESS,
            })
          );
        } finally {
          if (jobId) {
            zipDownloadGuardRef.current.activeJobs.delete(jobId);
            globalGuard.activeJobs.delete(jobId);
          }
        }
      };

      try {
        const res = await dispatch(exportThunk(param));
        const { data, status, fileName } = res?.payload || {};

        // Async contract: API returns JSON payload with job + status/download urls.
        const asyncPayload = await getJsonPayload(data, status);
        const asyncData = asyncPayload?.data || asyncPayload;
        if (
          status >= Common.API_STATUS_200 &&
          status < Common.API_STATUS_300 &&
          asyncData?.jobId &&
          asyncData?.socketUrl
        ) {
          try {
            await handleBackgroundZipDownload(asyncData);
          } catch (error) {
            dispatch(
              openSnackbar({
                message: error?.message || Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE,
                notificationType: Constants.NOTIFICATION_ERROR,
              })
            );
          }
          return;
        }

        if (!status || status >= Common.API_STATUS_400 || !fileName || !data) {
          const backendMessage = await resolveErrorMessage(data);
          dispatch(
            openSnackbar({
              message: backendMessage || Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE,
              notificationType: Constants.NOTIFICATION_ERROR,
            })
          );
          return;
        }

        const parsedFileName = fileName.split("filename=")[1];
        triggerBlobDownload(new Blob([data], { type: "application/zip" }), parsedFileName);

        dispatch(
          openSnackbar({
            message: Constants.ZIP_EXPORT_SUCCESS_MESSAGE,
            notificationType: Constants.NOTIFICATION_SUCCESS,
          })
        );
      } catch (error) {
        dispatch(
          openSnackbar({
            message: error?.message || Constants.ZIP_EXPORT_FALLBACK_ERROR_MESSAGE,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      } finally {
        setExportLoading(false);
      }
    };
    return (
      <WrappedComponent
        {...props}
        handleEquipmentSampleFileExport={handleEquipmentSampleFileExport}
        handleExportZipFile={handleExportZipFile}
        exportLoading={exportLoading}
        handlePdfExport={handlePdfExport}
        handleExcelExport={handleExcelExport}
      />
    );
  };
}

export default ExportHOC;
